import React from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { remittanceActions } from "../../state/remittanceSlice";
import "react-loading-skeleton/dist/skeleton.css";
import Button from "../../../../components/ui/Button";
import { styles } from "./styles";
import SelectDropdown from "../../components/ui/SelectDropdown";
import { Download, Eye, Signature } from "lucide-react";
import MixpanelConfig from "../../../../constants/MixpanelConfig";

interface ReviewOverviewProps {
  viewORDownloadA2Form: (type: string) => void;
  initializeTransaction: () => void;
}

const ReviewOverview: React.FC<ReviewOverviewProps> = ({
  viewORDownloadA2Form,
  initializeTransaction,
}) => {
  const dispatch = useAppDispatch();

  const authState = useAppSelector((state) => state.auth);
  const remitterState = useAppSelector((state) => state.remittance);
  const { calculator, remitter, review, transactionDetail } = useAppSelector(
    (state) => state.remittance
  );
  const { remitterSignedStatus } = review;

  const lanNumberLength = review?.reviewDetails?.lanNumber?.trim()?.length ?? 0;

  const expectedLanLength = Number(authState?.lanLength || 12);

  const payButtonDisable =
    review?.remitterSignedStatus?.loading ||
    review?.consentData?.loading ||
    review?.remitterSignedStatus?.error ||
    review?.consentData?.error ||
    !review?.reviewDetails?.consentForm ||
    !review?.reviewDetails?.termsAndConditions ||
    review?.reviewDetails?.sourceOfFundsDropdown == null ||
    (authState?.lanMandatory && lanNumberLength !== expectedLanLength);

  const payEsignedDisable = !(
    remitterSignedStatus.data?.signed ||
    remitterSignedStatus.data?.signLinkSent ||
    remitterSignedStatus.data?.signedLatter
  );

  const handleDownloadA2Form = () => {
    if (remitterSignedStatus.data?.signed || remitterSignedStatus.data?.signLinkSent) {
      viewORDownloadA2Form("download");
    } else {
      const userKey = remitter?.selectedRemitter?.userKey;
      if (!userKey) {
        return;
      }
      dispatch(remittanceActions.setModalType("REMITTER_ESIGN_MODAL"));
      dispatch(remittanceActions.fetchRemitterSignedStatusRequested(userKey));
    }
  };

  const loading =
    review?.remitterSignedStatus?.loading ||
    review?.consentData?.loading ||
    calculator?.getTransactionSummary?.loading;

  return (
    <div className={styles.reviewOverview.wrapper}>
      <div className={styles.reviewOverview.container}>
        <div className="mt-2 py-4 space-y-5">
          <div className="flex items-center gap-3">
            <div
              id="purpose"
              className="h-12 w-[460px] border border-gray-300 flex items-center gap-2 rounded-md p-4 bg-gray-100 text-[14px] cursor-not-allowed"
            >
              <span className="float-left font-semibold">Purpose of Payment:</span>
              {calculator?.amountCalculation?.transaction_purpose}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div
              id="vendorCode"
              className="h-12 w-[460px] border border-gray-300 flex items-center gap-2 rounded-md p-4 bg-gray-100 text-[14px] cursor-not-allowed"
            >
              <span className="float-left font-semibold">Vendor Code:</span>
              {calculator?.amountCalculation?.vendorDropdown?.vendorCode}
            </div>
          </div>

          {/* LAN/UTR fields are visible for all vendors for now. */}
          <>
            <div className="flex items-center gap-3">
              <div
                id="lanNO"
                className="h-12 w-[460px] border border-gray-300 flex items-center gap-2 rounded-md p-4 bg-gray-100 text-[14px]"
              >
                <span className="font-semibold whitespace-nowrap">
                  LAN No:
                  <span className="text-red-100 text-lg pl-1">
                    {authState?.lanMandatory ? "*" : ""}
                  </span>
                </span>
                <input
                  type="text"
                  value={review?.reviewDetails?.lanNumber}
                  className="w-full bg-gray-100 text-[14px] text-black outline-none flex-1 mt-1"
                  onChange={(e) => {
                    const input = e.target.value;
                    const alphanumericOnly = input.replace(/[^a-zA-Z0-9]/g, "");
                    if (alphanumericOnly.length <= expectedLanLength) {
                      dispatch(remittanceActions.setReviewDetailsInput({ lanNumber: input }));
                    }
                    MixpanelConfig.trackEvent(MixpanelConfig.eventNames.New_Review_LAN_Enter, {
                      authState: authState,
                      remitterState: remitterState,
                      lanValue: alphanumericOnly,
                    });
                  }}
                  placeholder={`Enter ${expectedLanLength} digit LAN Number`}
                  required={authState?.lanMandatory}
                  minLength={expectedLanLength}
                  maxLength={expectedLanLength}
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div
                id="utrNO"
                className="h-12 w-[460px] border border-gray-300 flex items-center gap-2 rounded-md p-4 bg-gray-100 text-[14px]"
              >
                <span className="font-semibold whitespace-nowrap">UTR No: </span>
                <input
                  type="text"
                  value={review?.reviewDetails?.utrNumber}
                  className="flex-1 w-full bg-gray-100 text-[14px] text-black outline-none"
                  onChange={(e) => {
                    const input = e.target.value;
                    dispatch(remittanceActions.setReviewDetailsInput({ utrNumber: input }));
                  }}
                  placeholder="Enter UTR Number"
                />
              </div>
            </div>
          </>
          {/* )} */}

          <span className="float-left font-semibold">
            Source of Funds
            <span className="text-red-100 text-sm pl-1">{"*"}</span>
          </span>
          <SelectDropdown
            data={review?.sourceOfFundsData ?? []}
            value={review?.reviewDetails?.sourceOfFundsDropdown ?? null}
            onChange={(response) => {
              dispatch(
                remittanceActions.setReviewDetailsInput({ sourceOfFundsDropdown: response })
              );
            }}
            getLabel={(item) => item.label}
            getValue={(item) => item.value}
            width={"460px"}
            placeHolderFontSize={"16px"}
            textSize={"16px"}
            fontWeight={100}
            backgroundColor="#f3f4f6"
            placeholder={"Select Source of Funds"}
            isSearchable={true}
            isLoading={calculator?.purpose?.loading}
          />

          <div className="flex gap-2">
            <Button
              bgColor={"bg-[#006EFF]"}
              fontStyle={{
                fontFamily: "font-Inter",
                fontSize: "text-sm",
                fontWeight: "font-bold",
                fontColor: "text-white",
              }}
              borderStyle={{ borderRadius: "rounded-lg" }}
              className={"p-3 hover:bg-blue-600 min-w-[10rem]"}
              onClickHandler={() => viewORDownloadA2Form("view")}
              disabled={review?.viewA2Form?.loading}
              isLoading={review?.viewA2Form?.loading}
            >
              <div className="flex items-center gap-1">
                <Eye width={15} height={15} />
                {remitterSignedStatus.data?.signed ? "Signed A2 Form" : "A2 Form"}
              </div>
            </Button>
            <Button
              bgColor={"bg-[#006EFF]"}
              fontStyle={{
                fontFamily: "font-Inter",
                fontSize: "text-sm",
                fontWeight: "font-bold",
                fontColor: "text-white",
              }}
              borderStyle={{ borderRadius: "rounded-lg" }}
              className={"p-3 hover:bg-blue-600 min-w-[10rem]"}
              onClickHandler={handleDownloadA2Form}
              disabled={review?.downloadA2Form?.loading}
              isLoading={review?.downloadA2Form?.loading}
            >
              <div className="flex items-center gap-1">
                {remitterSignedStatus.data?.signed || remitterSignedStatus.data?.signLinkSent ? (
                  <Download width={15} height={15} />
                ) : (
                  <Signature width={15} height={15} />
                )}
                {remitterSignedStatus.data?.signed
                  ? "Signed A2 Form"
                  : remitterSignedStatus.data?.signLinkSent
                  ? "A2 Form"
                  : "Sign A2 Form"}

                {payEsignedDisable && <span className="text-red-100 text-sm">{"*"}</span>}
              </div>
            </Button>
            {loading && (
              <div className="flex justify-center items-center">
                <div className="animate-spin h-6 w-6 border-2 border-t-blue-500 rounded-full" />
              </div>
            )}
          </div>

          <div className="flex flex-col space-y-2">
            <div className="flex">
              <input
                type="checkbox"
                checked={review?.reviewDetails?.termsAndConditions}
                onChange={() => {
                  dispatch(
                    remittanceActions.setReviewDetailsInput({
                      termsAndConditions: !review?.reviewDetails?.termsAndConditions,
                    })
                  );
                }}
                required
                className="review-checkbox w-11 h-11"
                style={{ cursor: "pointer" }}
              />

              <label className="ml-2 font-medium font-sans text-[16px] flex items-center">
                <p>
                  I confirm that the information captured in the A2 Form is accurate
                  <span> and agree to the</span>
                  <span
                    className=" text-blue-500 cursor-pointer hover:underline"
                    onClick={() => {
                      dispatch(remittanceActions.setModalType("TERMS_AND_CONDITIONS"));
                    }}
                  >
                    {" Terms & Conditions. "}
                  </span>
                </p>
              </label>
            </div>
            <div className="flex">
              <input
                type="checkbox"
                required
                checked={review?.reviewDetails?.consentForm}
                className="review-checkbox w-[70px] h-8"
                onClick={() => {
                  dispatch(
                    remittanceActions.setReviewDetailsInput({
                      consentForm: !review?.reviewDetails?.consentForm,
                    })
                  );
                }}
                style={{ cursor: "pointer" }}
              />
              <label className="ml-2 font-medium font-sans text-[16px] flex items-center">
                <p>
                  As the authorized representative of the Remitter, I hereby acknowledge and accept
                  the defined
                  <span
                    className="text-blue-600 cursor-pointer hover:underline"
                    onClick={() => {
                      dispatch(remittanceActions.setModalType("CONSENT_FORM"));
                    }}
                  >
                    {" "}
                    roles, responsibilities, and timelines{" "}
                  </span>
                  associated with the transaction.{" "}
                </p>
              </label>
            </div>
          </div>

          <Button
            bgColor={
              payButtonDisable || payEsignedDisable || transactionDetail?.loading
                ? "bg-primary-200 opacity-50"
                : "bg-primary-200"
            }
            fontStyle={{
              fontFamily: "font-Inter",
              fontSize: "text-sm",
              fontWeight: "font-bold",
              fontColor: "text-white",
            }}
            borderStyle={{ borderRadius: "rounded-lg" }}
            className={`p-4 ${
              payButtonDisable || payEsignedDisable || transactionDetail?.loading
                ? "hover:bg-primary-200 opacity-50"
                : "hover:bg-red-200"
            } min-w-[21rem] w-[450px] items-center rounded-3xl`}
            onClickHandler={initializeTransaction}
            disabled={payButtonDisable || payEsignedDisable || transactionDetail?.loading}
            isLoading={transactionDetail?.loading}
          >
            <div className="flex items-center font-bold">
              <p className="font-bold text-[20px]">{"PAY"}</p>
            </div>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReviewOverview;
